import type {
  BarInteraction,
  ConsentBehavior,
  ConsentConfig,
  ConsentTheme,
  EntryStyle,
  RuntimeConfig,
  ScriptConfig,
} from '../types';

const ENTRY_STYLES: EntryStyle[] = ['icon', 'icon_text', 'bottom_bar', 'top_bar'];
const BAR_INTERACTIONS: BarInteraction[] = ['open_modal', 'inline_actions'];

export const DEFAULT_THEME: ConsentTheme = {
  primary: '#4C6FFF',
  surface: '#121826',
  text: '#E7EAF2',
  muted: '#8B93A7',
  radius: 12,
  font: 'system',
  entry_position: 'bottom_left',
  modal_size: 'md',
  overlay: false,
  modal_anchor: false,
};

export const DEFAULT_BEHAVIOR: ConsentBehavior = {
  auto_open: true,
  blocking: false,
  reload_on_revoke: true,
  expiry_days: 180,
  consent_version: 1,
  google_consent_mode: false,
  respect_gpc: true,
};

function normalizeTheme(input: Partial<ConsentTheme> | undefined): ConsentTheme {
  const theme = input || {};
  const color = (key: keyof ConsentTheme): string => {
    const value = String(theme[key] ?? '');
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : (DEFAULT_THEME[key] as string);
  };

  return {
    primary: color('primary'),
    surface: color('surface'),
    text: color('text'),
    muted: color('muted'),
    radius: Number.isFinite(Number(theme.radius)) ? Number(theme.radius) : DEFAULT_THEME.radius,
    font: theme.font === 'inherit' ? 'inherit' : 'system',
    entry_position:
      theme.entry_position && ['bottom_left', 'bottom_right', 'top_left', 'top_right'].includes(theme.entry_position)
        ? theme.entry_position
        : DEFAULT_THEME.entry_position,
    modal_size: theme.modal_size && ['sm', 'md', 'lg'].includes(theme.modal_size) ? theme.modal_size : 'md',
    overlay: theme.overlay !== undefined ? Boolean(theme.overlay) : DEFAULT_THEME.overlay,
    modal_anchor: theme.modal_anchor !== undefined ? Boolean(theme.modal_anchor) : DEFAULT_THEME.modal_anchor,
  };
}

function normalizeBehavior(input: Partial<ConsentBehavior> | undefined): ConsentBehavior {
  const behavior = input || {};
  const bool = (key: keyof ConsentBehavior): boolean =>
    behavior[key] !== undefined ? Boolean(behavior[key]) : (DEFAULT_BEHAVIOR[key] as boolean);

  return {
    auto_open: bool('auto_open'),
    blocking: bool('blocking'),
    reload_on_revoke: bool('reload_on_revoke'),
    expiry_days: Number(behavior.expiry_days) > 0 ? Number(behavior.expiry_days) : DEFAULT_BEHAVIOR.expiry_days,
    consent_version: Number(behavior.consent_version) > 0 ? Number(behavior.consent_version) : 1,
    google_consent_mode: bool('google_consent_mode'),
    respect_gpc: bool('respect_gpc'),
  };
}

type LegacyScript = Partial<ScriptConfig> & {
  consent_required?: boolean;
  consent_category?: string;
};

export function parseConfig(input: Partial<RuntimeConfig> & { analytics?: unknown[] }): RuntimeConfig {
  const consentIn = (input.consent || {}) as Partial<ConsentConfig> & {
    banner_title?: string;
    description?: string;
    button_text?: { accept?: string; reject?: string };
  };

  const categories = normalizeCategories(
    Array.isArray(consentIn.categories) ? consentIn.categories : null,
  );

  const entryStyle = ENTRY_STYLES.includes(consentIn.entry?.style as EntryStyle)
    ? (consentIn.entry?.style as EntryStyle)
    : 'icon_text';
  const isBar = entryStyle === 'bottom_bar' || entryStyle === 'top_bar';
  const barInteraction = BAR_INTERACTIONS.includes(consentIn.entry?.bar_interaction as BarInteraction)
    ? (consentIn.entry?.bar_interaction as BarInteraction)
    : 'open_modal';

  const consent: ConsentConfig = {
    enabled: Boolean(consentIn.enabled ?? true),
    entry: {
      style: entryStyle,
      label: consentIn.entry?.label || consentIn.banner_title || 'Cookie settings',
      icon: consentIn.entry?.icon || 'default',
      bar_interaction: isBar ? barInteraction : 'open_modal',
      show_preferences:
        isBar && barInteraction === 'inline_actions'
          ? Boolean(consentIn.entry?.show_preferences)
          : false,
    },
    theme: normalizeTheme(consentIn.theme),
    behavior: normalizeBehavior(consentIn.behavior),
    modal: {
      mode: (consentIn.modal?.mode as 'blocks' | 'rich') || 'blocks',
      title: consentIn.modal?.title || consentIn.banner_title || 'Cookie Policy',
      blocks: Array.isArray(consentIn.modal?.blocks) ? consentIn.modal.blocks : [],
      rich_html: consentIn.modal?.rich_html || '',
    },
    actions: {
      accept_all: consentIn.actions?.accept_all || consentIn.button_text?.accept || 'Accept All Cookies',
      essential_only:
        consentIn.actions?.essential_only || consentIn.button_text?.reject || 'Use Essential Cookies Only',
      save_preferences: consentIn.actions?.save_preferences || 'Save preferences',
      edit_preferences: consentIn.actions?.edit_preferences || 'Edit preferences',
    },
    categories,
  };

  const scripts: ScriptConfig[] = (Array.isArray(input.scripts) ? input.scripts : []).map((raw) => {
    const script = raw as LegacyScript;
    return {
      id: String(script.id || ''),
      name: script.name || 'Script',
      description: script.description || '',
      type: script.type === 'analytics' ? 'analytics' : 'custom',
      enabled: Boolean(script.enabled),
      position: script.position || 'body_end',
      priority: Number(script.priority || 100),
      environment: script.environment || 'all',
      content: script.content || '',
      consent_categories: normalizeScriptCategories(script),
    };
  });

  return {
    version: Number(input.version || 0),
    site_key: String(input.site_key || ''),
    environment: String(input.environment || 'production'),
    preview: Boolean(input.preview),
    consent,
    scripts,
  };
}

function normalizeCategories(
  input: ConsentConfig['categories'] | null,
): ConsentConfig['categories'] {
  const essential: ConsentConfig['categories'][number] = {
    id: 'essential',
    label: 'Essential',
    description: 'Needed for the site to work.',
    required: true,
    ask_visitors: false,
  };

  // Trust the API payload, do not invent Analytics/Marketing/Functional.
  if (!input || input.length === 0) return [essential];

  const seen = new Set<string>();
  const categories: ConsentConfig['categories'] = input
    .map((category) => {
      let id = String(category.id || '');
      if (id === 'necessary') id = 'essential';
      const required = Boolean(category.required) || id === 'essential';
      return {
        id,
        label: category.label || id,
        description: category.description || '',
        required,
        ask_visitors: required
          ? false
          : category.ask_visitors !== undefined
            ? Boolean(category.ask_visitors)
            : true,
      };
    })
    .filter((category) => {
      if (!category.id || seen.has(category.id)) return false;
      seen.add(category.id);
      return true;
    });

  if (categories.length === 0) return [essential];

  if (!seen.has('essential')) {
    categories.unshift({ ...essential });
  } else {
    const row = categories.find((category) => category.id === 'essential');
    if (row) {
      row.required = true;
      row.ask_visitors = false;
    }
  }

  return categories;
}

function normalizeScriptCategories(script: LegacyScript): string[] {
  let categories: string[] = [];

  if (Array.isArray(script.consent_categories) && script.consent_categories.length > 0) {
    categories = script.consent_categories.map(String);
  } else if (script.consent_required === false) {
    categories = ['essential'];
  } else if (script.consent_category) {
    categories = [String(script.consent_category)];
  } else if (script.type === 'analytics') {
    categories = ['analytics'];
  } else {
    categories = ['essential'];
  }

  categories = [...new Set(categories.map((id) => (id === 'necessary' ? 'essential' : id)).filter(Boolean))];
  return categories.length > 0 ? categories : ['essential'];
}

/** True when script only needs required (essential) categories, may run before consent UI. */
export function isEssentialSafe(script: ScriptConfig, requiredIds: Set<string>): boolean {
  if (!script.consent_categories.length) return true;
  return script.consent_categories.every((id) => requiredIds.has(id));
}
